import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "src/components/DialogComponent";
import TGButton from "src/components/ui/TGButton";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { SelectIcon } from "./SelectIcon";

const WEEKDAYS_RU = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const MONTHS_RU = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

function normalizeDate(d: Date | null | undefined): Date | null {
  if (!d) return null;
  return moment(d).startOf("day").toDate();
}

function sameDay(a: Date | null | undefined, b: Date | null | undefined): boolean {
  if (!a || !b) return false;
  return moment(a).startOf("day").isSame(moment(b).startOf("day"), "day");
}

function isBetween(day: Date, from: Date | null | undefined, to: Date | null | undefined): boolean {
  const d = moment(day).startOf("day");
  const f = from ? moment(from).startOf("day") : null;
  const t = to ? moment(to).startOf("day") : null;
  if (f && d.isBefore(f)) return false;
  if (t && d.isAfter(t)) return false;
  return true;
}

function buildMonthGrid(year: number, month0: number): Date[] {
  const first = moment().year(year).month(month0).startOf("month");
  const firstMonday = first.clone().startOf("isoWeek");
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    days.push(firstMonday.clone().add(i, "days").toDate());
  }
  return days;
}

function SelectReviewDateMobile({
  title,
  initial,
  onResult,
  modalTitle,
  modalDescription,
  filters,
  zIndex,
}: {
  title: string;
  initial: Date;
  onResult: (date: Date) => void;
  modalTitle: string;
  modalDescription: string;
  filters?: {
    start: Date;
    end: Date;
  };
  zIndex?: number;
}) {
  const [openDialog, setOpenDialog] = useState(false);
  const [reviewDate, setReviewDate] = useState<Date | null>(() =>
    normalizeDate(initial) ?? null
  );
  const [showCalendar, setShowCalendar] = useState(false);
  const today = useMemo(() => normalizeDate(new Date())!, []);
  const [cursor, setCursor] = useState(() => {
    const base = normalizeDate(initial) ?? today;
    return { year: base.getFullYear(), month: base.getMonth() };
  });

  const prevInitialRef = useRef<Date | null>(normalizeDate(initial) ?? null);
  useEffect(() => {
    if (openDialog) {
      return;
    }
    const init = normalizeDate(initial) ?? null;
    const prev = prevInitialRef.current;
    const diff =
      prev?.getTime() !== init?.getTime() || (prev === null) !== (init === null);
    if (diff) {
      setReviewDate(init);
      if (init) {
        setCursor({ year: init.getFullYear(), month: init.getMonth() });
      } else {
        setCursor({ year: today.getFullYear(), month: today.getMonth() });
      }
      prevInitialRef.current = init;
    }
  }, [initial, openDialog, today]);

  function toggleDialog(state: boolean) {
    if (state) {
      const init = normalizeDate(initial) ?? null;
      setReviewDate(init);
      setShowCalendar(false);
      if (init) {
        setCursor({ year: init.getFullYear(), month: init.getMonth() });
      } else {
        setCursor({ year: today.getFullYear(), month: today.getMonth() });
      }
    }
    setOpenDialog(state);
  }

  useEffect(() => {
    if (!openDialog) {
      setShowCalendar(false);
    }
  }, [openDialog]);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!showCalendar) {
      return;
    }
    function onClickOutside(e: MouseEvent) {
      const root = wrapRef.current;
      if (!root) return;
      const target = e.target as HTMLElement;
      if (!root.contains(target)) {
        setShowCalendar(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [showCalendar]);

  function handleFieldClick() {
    setShowCalendar((prev) => !prev);
  }

  function handleClearButton(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    setReviewDate(null);
    setShowCalendar(true);
  }

  function handleCalendarButton(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    setShowCalendar((prev) => !prev);
  }

  function handlePickDay(day: Date) {
    const ok = isBetween(day, filters?.start ?? today, filters?.end ?? null);
    if (!ok) {
      return;
    }
    const picked = normalizeDate(day)!;
    setReviewDate(picked);
    setShowCalendar(false);
  }

  function backButton() {
    setOpenDialog(false);
  }

  function submitDate() {
    if (!reviewDate) {
      return;
    }

    setOpenDialog(false);
    onResult(reviewDate);
  }

  const days = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor.year, cursor.month]);
  const minDate = useMemo(() => normalizeDate(filters?.start) ?? today, [filters, today]);
  const maxDate = useMemo(() => normalizeDate(filters?.end) ?? null, [filters]);

  function prevMonth() {
    setCursor((c) => {
      const m = moment().year(c.year).month(c.month).subtract(1, "month");
      return { year: m.year(), month: m.month() };
    });
  }
  function nextMonth() {
    setCursor((c) => {
      const m = moment().year(c.year).month(c.month).add(1, "month");
      return { year: m.year(), month: m.month() };
    });
  }

  return (
    <FlexRow className="flex items-center justify-between p-5 font-medium text-black flex-nowrap">
      <FlexCol className="text-[18px] leading-[28px]">
        <span>{title}:</span>
        <span className="block">
          {reviewDate ? moment(reviewDate).locale("ru").format("DD.MM.YYYY") : "Укажите дату"}
        </span>
      </FlexCol>
      <FlexCol className="shrink-0">
        <button onClick={() => toggleDialog(true)}>
          <SelectIcon isSelect={!!reviewDate} />
        </button>
      </FlexCol>
      <Modal zIndex={zIndex ? zIndex + 10 : 70} open={openDialog} toggleDialog={toggleDialog}>
        <header className="px-10 pb-4">
          <h2>{modalTitle}</h2>
        </header>

        <div className="grow px-10 py-5">
          <h4 className="mb-2.5 text-black">{modalDescription}</h4>
          <div ref={wrapRef} className="relative">
            <div
              onClick={handleFieldClick}
              className="block w-full rounded-md border border-[#DFE4EA] py-3 pl-5 pr-16 text-base text-black outline-none shadow-custom cursor-pointer select-none bg-white"
            >
              <span className="block h-6 leading-6">
                {reviewDate ? moment(reviewDate).locale("ru").format("DD.MM.YYYY") : ""}
                {!reviewDate ? (
                  <span className="text-[#a0a4a8]">ДД.ММ.ГГГГ</span>
                ) : null}
              </span>
            </div>
            <div className="absolute right-0 top-0 h-full flex items-center pr-2 select-none gap-0.5">
              {reviewDate ? (
                <button
                  type="button"
                  onClick={handleClearButton}
                  aria-label="Очистить дату"
                  tabIndex={-1}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleCalendarButton}
                aria-label="Открыть календарь"
                tabIndex={-1}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700"
              >
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm-3 5h14v9H4V9zm3 2a1 1 0 011-1h.01a1 1 0 110 2H8a1 1 0 01-1-1zm4 0a1 1 0 011-1h.01a1 1 0 110 2H12a1 1 0 01-1-1zm-4 3a1 1 0 011-1h.01a1 1 0 110 2H8a1 1 0 01-1-1zm4 0a1 1 0 011-1h.01a1 1 0 110 2H12a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            {showCalendar ? (
              <div className="mt-2.5 w-full bg-white rounded-md border border-[#DFE4EA] shadow-custom overflow-hidden relative z-30">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <button
                    type="button"
                    className="text-gray-500 hover:text-gray-800 text-2xl leading-none w-8 h-8 flex items-center justify-center"
                    onClick={prevMonth}
                    aria-label="Предыдущий месяц"
                  >
                    ‹
                  </button>
                  <div className="text-[16px] font-semibold text-gray-900">
                    {MONTHS_RU[cursor.month]} {cursor.year}
                  </div>
                  <button
                    type="button"
                    className="text-gray-500 hover:text-gray-800 text-2xl leading-none w-8 h-8 flex items-center justify-center"
                    onClick={nextMonth}
                    aria-label="Следующий месяц"
                  >
                    ›
                  </button>
                </div>
                <div className="grid grid-cols-7 px-3 pt-3 pb-2 text-[12px] text-gray-500 font-medium">
                  {WEEKDAYS_RU.map((wd) => (
                    <div key={wd} className="text-center h-8 flex items-center justify-center">
                      {wd}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 px-3 pb-4">
                  {days.map((day, i) => {
                    const inMonth = day.getMonth() === cursor.month;
                    const isDisabled = !isBetween(day, minDate, maxDate);
                    const isToday = sameDay(day, today);
                    const isPicked = sameDay(day, reviewDate);
                    let cellCls =
                      "h-9 text-[14px] flex items-center justify-center rounded-md mx-0.5 cursor-pointer transition-colors ";
                    if (isDisabled) {
                      cellCls += " text-gray-300 cursor-not-allowed hover:bg-transparent ";
                    } else {
                      cellCls += inMonth ? " text-gray-900 " : " text-gray-400 ";
                      cellCls += " hover:bg-gray-100 ";
                    }
                    if (isToday && !isPicked) {
                      cellCls += " ring-1 ring-blue-400 ";
                    }
                    if (isPicked) {
                      cellCls += " !bg-blue-700 !text-white hover:!bg-blue-700 ";
                    }
                    return (
                      <button
                        key={i}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => handlePickDay(day)}
                        className={cellCls}
                      >
                        {day.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <footer className="mt-4 flex justify-between border-t px-10 pt-10">
          <TGButton onClick={backButton}>Отмена</TGButton>
          <TGButton disabled={!reviewDate} color="bluedark" onClick={submitDate}>
            Сохранить
          </TGButton>
        </footer>
      </Modal>
    </FlexRow>
  );
}

export default SelectReviewDateMobile;
